import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Card, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getRegions, createRegion, updateRegion, deleteRegion } from '../services/api';

const RegionManagement = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRegion, setEditingRegion] = useState(null);
  const [searchText, setSearchText] = useState('');

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const data = await getRegions();
      setRegions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      const errorMessage = error.message || 'Failed to load regions';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  const handleSubmit = async (values) => {
    try {
      const regionData = {
        name: values.name,
        description: values.description || null,
      };

      if (editingRegion) {
        await updateRegion(editingRegion.id, regionData);
        message.success('Region updated successfully');
      } else {
        await createRegion(regionData);
        message.success('Region created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingRegion(null);
      fetchRegions();
    } catch (error) {
      console.error('Error saving region:', error);
      message.error(error.message || 'Failed to save region');
    }
  };

  const handleEdit = (region) => {
    setEditingRegion(region);
    form.setFieldsValue({
      name: region.name,
      description: region.description || '',
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (regionId) => {
    try {
      await deleteRegion(regionId);
      message.success('Region deleted successfully');
      fetchRegions();
    } catch (error) {
      console.error('Error deleting region:', error);
      message.error(error.message || 'Failed to delete region');
    }
  };

  const filteredRegions = regions.filter((region) =>
    region.name.toLowerCase().includes(searchText.toLowerCase()) ||
    region.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || <span style={{ color: '#999' }}>No description</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            title="Delete"
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="region-management">
      <Card
        title="Region Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRegion(null);
              form.resetFields();
              setIsModalVisible(true);
            }}
          >
            Add Region
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} lg={8}>
            <Input
              placeholder="Search regions..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredRegions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title={editingRegion ? 'Edit Region' : 'Add New Region'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingRegion(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Region Name"
            rules={[
              { required: true, message: 'Please enter a region name' },
              { min: 2, message: 'Region name must be at least 2 characters' },
              { max: 100, message: 'Region name cannot exceed 100 characters' },
            ]}
          >
            <Input placeholder="Enter region name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 255, message: 'Description cannot exceed 255 characters' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRegion ? 'Update' : 'Create'} Region
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setEditingRegion(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RegionManagement;


